import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const TypeWriter = ({ text }) => {
  const [displayText, setDisplayText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText((prevText) => prevText + text[currentIndex]);
        setCurrentIndex((prevIndex) => prevIndex + 1);
      }, 50);

      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text]);

  return <span>{displayText}</span>;
};

const Services = () => {
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    navigate(path === "home" ? "/" : `/${path}`);
  };

  const heroText =
    "Let us handle your finances while you focus on growing your business. We offer a comprehensive range of services in one place, ensuring quality and reliability every step of the way.";

  useEffect(() => {
    const videoContainer = document.querySelector(".video-container");

    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    if (videoContainer) {
      videoContainer.addEventListener("touchstart", preventDefault, {
        passive: false,
      });
      videoContainer.addEventListener("touchmove", preventDefault, {
        passive: false,
      });
      videoContainer.addEventListener("touchend", preventDefault, {
        passive: false,
      });
      videoContainer.addEventListener("click", preventDefault);
    }

    return () => {
      if (videoContainer) {
        videoContainer.removeEventListener("touchstart", preventDefault);
        videoContainer.removeEventListener("touchmove", preventDefault);
        videoContainer.removeEventListener("touchend", preventDefault);
        videoContainer.removeEventListener("click", preventDefault);
      }
    };
  }, []);

  return (
    <div className="min-h-screen relative">
      {/* Video Background Section */}
      <div className="relative h-[50vh] md:h-screen overflow-hidden">
        <div className="video-container absolute inset-0 bg-black/40">
          <div
            className="absolute inset-0 z-0 pointer-events-none select-none touch-none"
            style={{
              WebkitTouchCallout: "none",
              WebkitUserSelect: "none",
              KhtmlUserSelect: "none",
              MozUserSelect: "none",
              msUserSelect: "none",
              userSelect: "none",
            }}
          >
            <video
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              controls={false}
              playsinline="true"
              webkit-playsinline="true"
              controlsList="nodownload nofullscreen noremoteplayback"
              disablePictureInPicture
              style={{
                pointerEvents: "none",
                touchAction: "none",
              }}
            >
              <source src="/videos/services_tax_video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        {/* Hero Text Overlay */}
        <div className="relative h-full flex items-center justify-end text-white px-8 md:px-16 max-w-[1400px] mx-auto pointer-events-auto">
          <div className="max-w-2xl text-right">
            <p className="text-base md:text-3xl font-light leading-relaxed backdrop-blur-sm bg-gray-900/30 p-4 md:p-6 rounded-lg">
              <TypeWriter text={heroText} />
              <span className="animate-pulse">|</span>
            </p>
          </div>
        </div>
      </div>

      {/* Services Content */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            What services and solutions can we offer to meet your needs
          </h1>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {/* Accounting Services */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 text-red-400 mb-4">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 3h18v18H3V3z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7 7h10M7 12h10M7 17h10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                Accounting Services
              </h3>
            </div>

            {/* Financial Statements */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 text-red-400 mb-4">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 2v6h6M16 13H8M16 17H8M10 9H8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                Financial Statements
              </h3>
            </div>

            {/* 1099 Filing */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 text-red-400 mb-4">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 2v6h6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">1099 Filing</h3>
            </div>

            {/* Sales Tax Compliance */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 text-red-400 mb-4">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 11V6M15 11V6M12 3v14M12 17l3 4M12 17l-3 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                Sales Tax Compliance
              </h3>
            </div>

            {/* Custom Reporting/Analytics */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 text-red-400 mb-4">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 20V10M12 20V4M6 20v-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                Custom Reporting/Analytics
              </h3>
            </div>

            {/* Tax Return Filing */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 text-red-400 mb-4">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22 12h-6l-2 3h-4l-2-3H2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M5 5v14M19 5v14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                Tax Return Filing
              </h3>
            </div>

            {/* State and Federal Tax Compliance */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 text-red-400 mb-4">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 11V6M15 11V6M12 3v14M12 17l3 4M12 17l-3 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                State and Federal Tax Compliance
              </h3>
            </div>

            {/* Tax Extension */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 text-red-400 mb-4">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                Tax Extension
              </h3>
            </div>

            {/* Tax Estimate */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 text-red-400 mb-4">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 14c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                Tax Estimate
              </h3>
            </div>

            {/* Tax Credits */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 text-red-400 mb-4">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20.88 14.88l-2.83 2.83M3.12 14.88l2.83 2.83M20.88 9.12l-2.83-2.83M3.12 9.12l2.83-2.83"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">Tax Credits</h3>
            </div>

            {/* Business Consulting */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 text-red-400 mb-4">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 7h-4V3M4 17h4v4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20 3l-5 5M4 21l5-5M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                Business Consulting
              </h3>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-12 text-center">
            <p className="text-xl text-gray-600 mb-6">
              Ready to get started? Contact us for a consultation.
            </p>
            <button
              onClick={() => handleNavigation("contact")}
              className="bg-red-400 text-white px-8 py-4 rounded-lg inline-block hover:bg-red-500 transition-colors text-lg shadow-md hover:shadow-lg"
            >
              Book an Appointment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
